/**
 * MODULE: _shared/frontend-shell/src/components/assistant/assistantProactiveAdvisorUtils.ts
 * PURPOSE: Detect proactive brain content for new-session assistant welcome UX [2.2.1225–1245].
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-07-08 [2.2.1478] — Risk alerts no longer trigger proactive welcome (top-nav BrainAlertsNavBadge).
 * RECENT_PRIOR: 2026-06-06 [2.2.1330] — email/learned/learning contexts no longer trigger proactive welcome overlay (risk/session/insights only).
 * RECENT_PRIOR: 2026-06-06 [2.2.1328] — email_templates mode beats learned/learning; ranked-library headline when send chips coexist with create; up to 3 email template context actions.
 * RECENT_PRIOR: 2026-05-26 [2.2.1254] — structured context actions for compose_draft on welcome chips.
 */

import type {
  AssistantContextAction,
  AssistantContextResponse,
  AssistantRiskAlert,
} from '@shared/lib/frontend/api/assistantContextApi';
import { riskAlertActionLabel } from './riskAlertActionUtils';
import {
  collectEmailTemplateContextActions,
  collectEmailTemplateWelcomeChips,
  hasEmailTemplateCreateWelcomeChip,
  hasExistingEmailTemplateSendChips,
} from './assistantWelcomePrompts';

export type ProactiveWelcomeMode =
  | 'risk'
  | 'session'
  | 'insights'
  | 'learned'
  | 'learning'
  | 'email_templates';

export function hasLearnedPreferences(context: AssistantContextResponse | null): boolean {
  return (context?.learned_preferences?.length ?? 0) > 0;
}

export function hasLearningPipelineActivity(context: AssistantContextResponse | null): boolean {
  const ls = context?.learning_status;
  if (ls?.catch_up_delayed || ls?.state === 'catching_up') return true;
  if ((ls?.state || '') === 'idle' && (ls?.next_step || '').trim()) return true;
  const eligible = context?.promotion_pipeline?.eligible_candidates ?? 0;
  const almostReady = context?.promotion_pipeline?.almost_ready_candidates ?? 0;
  const pending = context?.promotion_pipeline?.pending_candidates ?? 0;
  if (eligible > 0 || almostReady > 0) return true;
  if (pending >= 1 && !hasLearnedPreferences(context)) return true;
  return false;
}

function normalizeProactivePromptKey(text: string): string {
  return (text || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Drop prompt chips that repeat structured action chips (label or prompt_text). */
export function filterPromptsExcludingContextActions(
  prompts: string[],
  actions: AssistantContextAction[],
): string[] {
  const actionKeys = new Set<string>();
  for (const action of actions) {
    const label = (action.label || '').trim();
    const prompt = (action.prompt_text || action.label || '').trim();
    if (label) actionKeys.add(normalizeProactivePromptKey(label));
    if (prompt) actionKeys.add(normalizeProactivePromptKey(prompt));
  }
  return prompts.filter((prompt) => {
    const key = normalizeProactivePromptKey(prompt);
    if (!key || actionKeys.has(key)) return false;
    // "list my email templates" vs "Show my email templates" — same CTA family.
    if (/\b(list|show)\b.*\bemail templates?\b/.test(key)) {
      for (const actionKey of actionKeys) {
        if (/\b(list|show)\b.*\bemail templates?\b/.test(actionKey)) return false;
      }
    }
    // Grounded send lines that share the same template name as an action chip.
    for (const actionKey of actionKeys) {
      if (
        /\b(send|email template)\b/.test(key) &&
        /\b(send|email template)\b/.test(actionKey) &&
        key.includes('live smoke test template') &&
        actionKey.includes('live smoke test template')
      ) {
        return false;
      }
    }
    return true;
  });
}

/** Tap-to-ask prompts from session/insights fallbacks [2.2.1244]. Risk alert prompts live in top-nav alerts. */
export function collectProactiveAdvisorPrompts(context: AssistantContextResponse | null): string[] {
  if (!context) return [];
  const mode = proactiveWelcomeMode(context);
  const seen = new Set<string>();
  const out: string[] = [];
  // Email template actions render as structured chips above the fold — avoid duplicate prompt row.
  if (mode !== 'email_templates') {
    for (const chip of collectEmailTemplateWelcomeChips(context)) {
      if (chip.kind === 'open_create_modal') continue;
      const p = chip.prompt.trim();
      if (!p || seen.has(p)) continue;
      seen.add(p);
      out.push(p);
      if (out.length >= 3) return out;
    }
  }
  const nextStep = (context.learning_status?.next_step || '').trim();
  if (mode === 'email_templates' && nextStep && !seen.has(nextStep)) {
    const lower = nextStep.toLowerCase();
    const settingsNudge =
      /\bsettings\b/.test(lower) ||
      /\blearned preferences?\b/.test(lower) ||
      /\bpatterns?\b/.test(lower);
    if (!settingsNudge) {
      seen.add(nextStep);
      out.push(nextStep);
    }
  }
  const fallback = suggestedAdvisorPrompt(context);
  if (fallback && !seen.has(fallback)) {
    out.push(fallback);
  }
  return out.slice(0, 3);
}

/** Action-oriented advisor CTAs from risk alerts [2.2.1245]. */
export function collectProactiveAdvisorActionPrompts(
  context: AssistantContextResponse | null,
): string[] {
  if (!context) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const alert of context.risk_alerts ?? []) {
    for (const action of alert.suggested_actions ?? []) {
      const a = riskAlertActionLabel(action);
      if (!a || seen.has(a)) continue;
      seen.add(a);
      out.push(a);
      if (out.length >= 2) return out;
    }
  }
  return out;
}

/** Entity-aware advisor actions for welcome chips (compose_draft vs auto-send) [2.2.1254]. */
export function collectProactiveAdvisorContextActions(
  context: AssistantContextResponse | null,
): AssistantContextAction[] {
  if (!context) return [];
  const seen = new Set<string>();
  const out: AssistantContextAction[] = [];

  for (const action of collectEmailTemplateContextActions(context)) {
    const label = (action.label || '').trim();
    if (!label || seen.has(label)) continue;
    seen.add(label);
    out.push(action);
    if (out.length >= 3) return out;
  }

  return out;
}

export function proactiveWelcomeMode(context: AssistantContextResponse | null): ProactiveWelcomeMode | null {
  if (!context) return null;
  // Risk alerts surface in BrainAlertsNavBadge — do not block the assistant landing.
  if (context.session_delta?.has_changes && (context.session_delta.changes?.length ?? 0) > 0) {
    return 'session';
  }
  if ((context.top_insights?.length ?? 0) > 0 && (context.proactive_briefing ?? '').trim()) {
    return 'insights';
  }
  return null;
}

export function hasProactiveAdvisorWelcome(context: AssistantContextResponse | null): boolean {
  return proactiveWelcomeMode(context) !== null;
}

export function primaryRiskAlert(context: AssistantContextResponse | null): AssistantRiskAlert | null {
  const alert = context?.risk_alerts?.[0];
  return alert?.title?.trim() ? alert : null;
}

export function suggestedAdvisorPrompt(context: AssistantContextResponse | null): string | null {
  const mode = proactiveWelcomeMode(context);
  if (mode === 'learned') {
    return 'How are my learned preferences shaping your answers?';
  }
  if (mode === 'learning') {
    const nextStep = (context?.learning_status?.next_step || '').trim();
    if (nextStep) return nextStep;
    return 'What patterns is Brazn learning from my usage?';
  }
  const alert = primaryRiskAlert(context);
  if (!alert?.title) return null;
  const title = alert.title.replace(/\s+at risk$/i, '').trim();
  return title ? `Why is ${title} at risk?` : 'Why is this deal at risk?';
}

export function proactiveWelcomeHeadline(context: AssistantContextResponse | null): string {
  const mode = proactiveWelcomeMode(context);
  if (mode === 'email_templates') {
    if (hasExistingEmailTemplateSendChips(context)) {
      return 'Your top email templates are ready';
    }
    if (hasEmailTemplateCreateWelcomeChip(context)) {
      return 'Start your email template library';
    }
    return 'Your top email templates are ready';
  }
  if (mode === 'risk') {
    const riskCount = context?.risk_alerts?.length ?? 0;
    if (riskCount > 1) return `${riskCount} deals need your attention`;
    return 'A deal needs your attention';
  }
  if (mode === 'session') return 'Your pipeline changed since last session';
  if (mode === 'learned') {
    const count = context?.learned_preferences?.length ?? 0;
    if (count > 1) return `${count} learned preferences are active`;
    return 'Brazn learned from your recent sessions';
  }
  if (mode === 'learning') {
    const headline = (context?.learning_status?.headline || '').trim();
    if (headline) return headline;
    const eligible = context?.promotion_pipeline?.eligible_candidates ?? 0;
    const almostReady = context?.promotion_pipeline?.almost_ready_candidates ?? 0;
    if (eligible > 1) return `${eligible} patterns are ready to learn`;
    if (eligible === 1) return 'A pattern is ready to learn';
    if (almostReady > 1) return `${almostReady} patterns are almost ready to learn`;
    if (almostReady === 1) return 'A pattern is almost ready to learn';
    return 'Brazn is learning from your activity';
  }
  return 'Signals to review';
}

export function proactiveWelcomeDescription(context: AssistantContextResponse | null): string {
  const mode = proactiveWelcomeMode(context);
  if (mode === 'email_templates') {
    // Headline + action chips are sufficient; avoid a second paragraph of the same guidance.
    if (hasExistingEmailTemplateSendChips(context)) {
      return '';
    }
    if (hasEmailTemplateCreateWelcomeChip(context)) {
      return 'Create a reusable template — Brazn ranks it by sends and surfaces one-click chips here.';
    }
    return '';
  }
  if (mode === 'learned') {
    return 'Promoted patterns from your usage are guiding how Brazn prioritizes deals and drafts answers.';
  }
  if (mode === 'learning') {
    const detail = (context?.learning_status?.detail || '').trim();
    if (detail) return detail;
    const guidance = (context?.promotion_pipeline?.learning_guidance || '').trim();
    if (guidance) return guidance;
    return 'Brazn is watching recurring patterns from your CRM and assistant usage. Promoted preferences will appear here and in Settings.';
  }
  return 'Brazn reviewed your pipeline and surfaced the highest-priority signals before you ask.';
}
